import { useEffect, useRef, useState, type FC } from 'react';
import styles from './index.module.scss';
import carGIcon from '@assets/images/pump/car-g.svg';
import PumpList from '@components/pump-list';
import copyIcon from '@assets/images/pump/copy.svg';
import carProgressIcon from '@assets/images/pump/car-process.svg';
import Top from '@components/top';
import { useNavigate } from 'react-router-dom';
import Loading from '@ui/loading';
import { Decimal } from 'turbos-clmm-sdk';
import { pumpSdk } from '@libs/pump';
import Footer from '@components/footer';
import { formatContract } from '@utils/format-contract';
import { toast } from 'react-toastify';
import { useCopyToClipboard } from 'react-use';
import type { OpenapiClientPump } from 'src/openapi/pump';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import startBtnHoverJSON from '@assets/lottie/pump/start-btn-hover.json';
import Avatar from '@components/avatar';
import { useSuiNSName } from '@hooks/use-sui-ns-name';

const Pump: FC = () => {
  const navigate = useNavigate();
  const [isAnimate, setIsAnimate] = useState(false);
  const [, copy] = useCopyToClipboard();
  const [kingData, setKingData] = useState<OpenapiClientPump.GetPoolsResponse['king']>();
  const [isKing, setIsKing] = useState(true);

  const lottieRef = useRef<LottieRefCurrentProps>(null);

  const handleMouseEnter = () => {
    lottieRef.current?.play();
  };

  const handleMouseLeave = () => {
    lottieRef.current?.pause();
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (kingData?.token_address) {
      setIsAnimate(true);
      timer = setTimeout(() => {
        setIsAnimate(false);
      }, 1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [kingData?.token_address]);

  const suiNames = useSuiNSName(kingData?.created_by || '');

  let ratio = new Decimal(kingData?.real_sui_reserves || '0')
    .div(pumpSdk.fair_launch_sui_amount)
    .mul(100)
    .toFixed(2);

  ratio = Number(ratio) > 100 ? '100' : ratio;

  const data = kingData;
  return (
    <>
      <Top />
      {/* <Banner /> */}
      <div className={`${styles.start_btn} ${styles.start_btn_mobile}`}>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={styles.button}
          onClick={() => navigate('/launch')}
        >
          <Lottie
            lottieRef={lottieRef}
            animationData={startBtnHoverJSON}
            autoplay={false}
            loop
            className={styles.lottie}
          />
        </div>
      </div>

      <div className={styles.king}>
        <img src={carGIcon} />
        <div className={styles.king_text}>{isKing ? 'King of Racing' : 'Now Trending'}</div>
      </div>

      {!data ? (
        <div className={styles.loading}>
          <Loading size={36} />
        </div>
      ) : (
        <div
          className={`${styles.first} ${isAnimate ? styles.animate : ''}`}
          onClick={() => navigate(`/fun/${data.token_address}`)}
        >
          <div className={styles.icon}>
            <img src={data.token_metadata.iconUrl} />
          </div>

          <div className={styles.content}>
            <div className={styles.title}>
              {data.name} (${data.symbol})
            </div>
            <div className={styles.contract}>
              CA: <span>{formatContract(data.token_address)}</span>
              <img
                src={copyIcon}
                onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                  event.stopPropagation();
                  copy(data.token_address);
                  toast('Address is copied');
                }}
              />
            </div>

            <div className={styles.description}>{data.description}</div>
            <div className={styles.line}>
              <p>Created by:</p>
              <p className={styles.address}>
                <Avatar address={data.created_by} />
                {suiNames || data.created_by.slice(data.created_by.length - 8)}
              </p>
            </div>
            <div className={styles.line}>
              <p>Bonding Curve Progress:</p>
              <p className={styles.liquidity}>{ratio}%</p>
            </div>
            <div className={styles.percent}>
              <div className={styles.percent_bg}></div>
              <div className={styles.width} style={{ width: `${ratio}%` }}></div>
              <img src={carProgressIcon} style={{ left: `${ratio}%` }} />
            </div>
          </div>
        </div>
      )}

      <div className={`${styles.start_btn} ${styles.start_btn_pc}`}>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={styles.button}
          onClick={() => navigate('/launch')}
        >
          <Lottie
            lottieRef={lottieRef}
            animationData={startBtnHoverJSON}
            autoplay={false}
            loop
            className={styles.lottie}
          />
        </div>
      </div>

      <PumpList onChangeKingData={setKingData} onChangeIsKing={setIsKing} />
      <Footer />
    </>
  );
};

export default Pump;
